import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import getEnvironmentConfig from '../config/environment';
import {
  orchestrateDocument,
  generateDocument,
  downloadDocument,
  downloadDocumentAuthenticated,
  callAiDraftingAgent,
  viewDocumentAsHtml,
  getDocumentKey,
  generateDocumentsDirect,
  checkJobStatus,
  DOC_CREATION_API_BASE as DOC_CREATION_API_BASE_IMPORT
} from '../services/documentService';
import { getActiveSessionId, startNewClient, resetSessionCache } from '../utils/sessionManagerV2';
import { getCurrentUserInfo } from '../utils/auth';

// Progress tracking component for long-running operations
const GenerationProgress = ({ startTime, documentType }) => {
  const [elapsed, setElapsed] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    
    return () => clearInterval(interval);
  }, [startTime]);
  
  const isPackage = documentType?.toLowerCase().includes('package');
  const expectedTime = isPackage ? 75 : 30;
  const progress = Math.min((elapsed / expectedTime) * 100, 95); // Cap at 95% until complete
  
  return (
    <div className="p-4 bg-blue-50 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-blue-900">
          Generating {documentType}...
        </span>
        <span className="text-sm text-blue-700">
          {elapsed}s / ~{expectedTime}s
        </span>
      </div>
      <div className="w-full bg-blue-200 rounded-full h-2">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
          style={{ width: `${progress}%` }}
        />
      </div>
      <p className="text-xs text-blue-700 mt-2">
        {isPackage 
          ? "Creating 5 documents: Will, Trust, Financial POA, Healthcare POA, HIPAA Authorization"
          : "Preparing your document..."
        }
      </p>
    </div>
  );
};

// Removed - now using sessionManager.js for consistent session IDs

// Feature flag — set VITE_OA_ROUTING_ENABLED=true to enable chat-driven
// routing to the OA Business / FLP packages. Off by default until the UI
// surface (OperatingAgreementForm + integration smoke test) is validated.
const OA_ROUTING_ENABLED =
  typeof import.meta !== 'undefined' &&
  import.meta.env &&
  import.meta.env.VITE_OA_ROUTING_ENABLED === 'true';

// Function to detect document type from user message
function detectDocumentType(text) {
  const lowercaseText = text.toLowerCase();

  // Operating Agreement family — feature-flagged.
  // FLP keywords take precedence over generic "operating agreement" because
  // an FLP is also referred to as an operating agreement in practice.
  if (OA_ROUTING_ENABLED) {
    if (
      lowercaseText.includes('family limited partnership') ||
      /\bflp\b/.test(lowercaseText)
    ) {
      return 'Operating Agreement Package - FLP';
    }
    if (
      lowercaseText.includes('operating agreement') ||
      lowercaseText.includes('llc agreement')
    ) {
      return 'Operating Agreement Package - Business';
    }
  }
  
  // Check for packages first
  if (lowercaseText.includes('disclaimer package') || 
      (lowercaseText.includes('package') && 
       (lowercaseText.includes('estate') || lowercaseText.includes('disclaimer')))) {
    return 'Disclaimer Package';
  }
  
  if (lowercaseText.includes('healthcare') && lowercaseText.includes('package')) {
    return 'Healthcare Documents Package';
  }

  // Check for Fractional QTIP Package
  if ((lowercaseText.includes('fractional') && lowercaseText.includes('qtip')) ||
      (lowercaseText.includes('qtip') && lowercaseText.includes('package'))) {
    return 'Fractional QTIP Package';
  }

  // Check for Probate Avoidance Package
  if ((lowercaseText.includes('probate') && lowercaseText.includes('avoidance')) ||
      (lowercaseText.includes('probate') && lowercaseText.includes('package'))) {
    return 'Probate Avoidance Package';
  }

  // Check for will and trust together (package indicator)
  if ((lowercaseText.includes('will') && lowercaseText.includes('trust')) &&
      !lowercaseText.includes(' or ')) {
    return 'Disclaimer Package';
  }
  
  // Individual documents
  if (lowercaseText.includes('will') && !lowercaseText.includes('living will')) {
    return 'Will';
  }
  
  if (lowercaseText.includes('trust')) {
    return 'Trust';
  }
  
  if (lowercaseText.includes('power of attorney') || lowercaseText.includes('poa')) {
    if (lowercaseText.includes('financial')) {
      return 'Financial Power of Attorney';
    }
    if (lowercaseText.includes('healthcare') || lowercaseText.includes('medical')) {
      return 'Healthcare Power of Attorney';
    }
    return 'Power of Attorney';
  }
  
  if (lowercaseText.includes('hipaa')) {
    return 'HIPAA Authorization';
  }
  
  if (lowercaseText.includes('advance directive') || lowercaseText.includes('living will')) {
    return 'Advance Directive';
  }
  
  // Generic single-document "operating agreement" route — preserved for
  // backward compat when the package flag is off. Returns the document-type
  // name only; the orchestrator handles single-doc requests separately
  // from packaged requests.
  if (lowercaseText.includes('operating agreement')) {
    return 'Operating Agreement';
  }

  return null;
}

function detectAndParseCSVData(text) {
  // Check if the text contains CSV-like data with multiple rows
  const lines = text.trim().split('\n');
  
  // Must have at least 5 lines to be considered complete CSV data
  if (lines.length < 5) return null;
  
  // Check if it looks like the table format from backend (space separated)
  // Backend returns: "Placeholder    Description    Value"
  const looksLikeTable = lines.some(line => 
    line.includes('Placeholder') && line.includes('Description') && line.includes('Value')
  );
  
  // Check for different separator types
  const hasTabSeparators = lines.some(line => line.includes('\t'));
  const hasCommaSeparators = lines.some(line => line.includes(','));
  const hasMultipleSpaces = lines.some(line => /\s{2,}/.test(line)); // 2+ spaces
  
  if (!hasTabSeparators && !hasCommaSeparators && !hasMultipleSpaces) return null;
  
  // Try to parse the CSV data
  const variables = {};
  let rowCount = 0;
  
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine) continue;
    
    // Skip header rows and separator lines
    if (trimmedLine.toLowerCase().includes('placeholder') && 
        trimmedLine.toLowerCase().includes('value')) {
      continue;
    }
    if (trimmedLine.includes('----')) {
      continue;
    }
    
    // Parse the row based on detected format
    let parts = [];
    
    // For the table format from backend, use space separation
    if (looksLikeTable || hasMultipleSpaces) {
      // Split by 2 or more spaces (matches backend table format)
      parts = trimmedLine.split(/\s{2,}/);
    } else if (hasTabSeparators) {
      parts = trimmedLine.split('\t');
    } else if (hasCommaSeparators) {
      // Simple CSV parsing (doesn't handle quoted values with commas)
      parts = trimmedLine.split(',');
    }
    
    if (parts.length >= 2) {
      // The placeholder is in the first column - DON'T add {{}} as backend doesn't expect them
      const placeholder = parts[0].trim().replace(/[{}"]/g, '');
      
      // For space-separated data with 3 columns (Placeholder, Description, Value)
      // the value is in the third column (index 2)
      let value = '';
      if (parts.length >= 3 && looksLikeTable) {
        value = parts[2].trim();
      } else if (parts.length === 2) {
        // For 2-column data (CSV format), value is in second column
        value = parts[1].trim();
      } else {
        // Fallback to last column
        value = parts[parts.length - 1].trim();
      }
      
      // Skip empty values or header remnants
      if (placeholder && value && value.toLowerCase() !== 'value' && !value.includes('----')) {
        // Store without {{}} - backend expects plain placeholder names
        variables[placeholder] = value;
        rowCount++;
      }
    }
  }
  
  // Return null if we didn't find enough data rows
  if (rowCount < 3) return null;
  
  return variables;
}

function ChatInterface({
  setDocUrl,
  setDocContent,
  setDocKey,
  onDocumentCreated,
  currentDocKey,
  onDraftRevision,
  setIsPackage,
  setPackageUrls,
  isPackage,
  DOC_CREATION_API_BASE
}) {
  // Get the correct API endpoint from environment configuration
  const config = getEnvironmentConfig();
  const apiEndpoint = DOC_CREATION_API_BASE || 
    config.aws_cloud_logic_custom.find(api => api.name === 'DocCreationAPI')?.endpoint;
  
  if (!apiEndpoint) {
    console.error('Failed to get DocCreationAPI endpoint from environment config', { config });
  }

  console.log('ChatInterface mounted/updated with props:');
  console.log('- setDocUrl:', setDocUrl);
  console.log('- typeof setDocUrl:', typeof setDocUrl);
  console.log('- currentDocKey:', currentDocKey);
  console.log('- isPackage:', isPackage);
  console.log('- Using API endpoint:', apiEndpoint);
  console.log('- Hostname:', window.location.hostname);
  console.log('- Environment config:', config);
  
  // Use useRef to track if the initial message has been added
  // This persists across re-renders and doesn't trigger component updates
  const initialMessageRef = useRef(false);
  
  const [messages, setMessages] = useState(() => {
    // Initialize messages with the welcome message directly in the state initialization
    // This ensures it's only added once when the component mounts
    const initialMessage = {
      role: 'assistant',
      content: 'Welcome to TrustIQ! I can help you:\n\n📄 **Create new documents** - Just tell me what type you need\n✏️ **Edit existing documents** - Provide the document name or say "edit my trust"\n📦 **Create document packages** - Ask for a "Disclaimer Package" or estate planning set\n\nWhat would you like to do today?',
      docUrl: null
    };
    return [initialMessage];
  });
  
  const [userInput, setUserInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [draftingInProgress, setDraftingInProgress] = useState(false);
  const [currentDocType, setCurrentDocType] = useState(null);
  const [awaitingInfo, setAwaitingInfo] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);
  const [isCollectingPlaceholders, setIsCollectingPlaceholders] = useState(false);
  const [workflowMode, setWorkflowMode] = useState(null); // 'create' | 'edit' | null
  const [editingDocKey, setEditingDocKey] = useState(null);
  
  // Function to reset state for a new document
  const resetForNewDocument = () => {
    setCurrentDocType(null);
    setDraftingInProgress(false);
    setAwaitingInfo(null);
    setIsCollectingPlaceholders(false);
    setWorkflowMode(null);
    setEditingDocKey(null);
    // Reset session cache (new session will be fetched when needed)
    resetSessionCache();
    setSessionId(null);
    if (setDocKey) setDocKey(null);
    if (setDocUrl) setDocUrl(null);
    if (setDocContent) setDocContent(null);
    if (setIsPackage) setIsPackage(false);
    if (setPackageUrls) setPackageUrls(null);
  };
  
  useEffect(() => {
    console.log("currentDocKey changed:", currentDocKey);
  }, [currentDocKey]);

  // Initialize session and user info on component mount
  useEffect(() => {
    const initializeSession = async () => {
      console.log('ChatInterface mounted - initializing session');
      
      // Get user info
      const user = await getCurrentUserInfo();
      setUserInfo(user);

      // Get active session ID from server
      const sid = await getActiveSessionId();
      setSessionId(sid);
      console.log('Session initialized:', sid);
    };
    
    initializeSession();
  }, []); // Empty dependency array = runs once on mount

  const addMessage = (role, content, docUrl = null, packageUrls = null) => {
    console.log(`addMessage called - role: ${role}, content length: ${content?.length}`);
    console.log(`Content preview: ${content?.substring(0, 200)}...`);
    console.log(`docUrl passed to addMessage:`, docUrl);
    
    if (content?.includes('---- Copy the table below to a spreadsheet ----')) {
      console.log('Message contains CSV table delimiter!');
    }
    setMessages(prev => [...prev, { role, content, docUrl, packageUrls }]);
    
    // Automatically call setDocUrl when a docUrl is present
    if (docUrl && setDocUrl) {
      console.log('🔵 addMessage auto-setting docUrl:', docUrl);
      console.log('🔵 setDocUrl function type:', typeof setDocUrl);
      setDocUrl(docUrl);
      
      // Verify the call was made
      setTimeout(() => {
        console.log('🔵 Verification: setDocUrl was called with:', docUrl);
      }, 50);
    } else if (docUrl && !setDocUrl) {
      console.error('❌ ERROR: docUrl provided but setDocUrl function is missing!');
    }
  };

  // Helper to ensure session is loaded before API calls
  const ensureSessionId = async () => {
    if (sessionId) {
      return sessionId;
    }
    console.log('Session not cached, fetching from server...');
    const sid = await getActiveSessionId();
    setSessionId(sid);
    return sid;
  };

  // No need for an initial greeting useEffect since we included it in the initial state

  // Helper function to detect if user is requesting a new document
  const isNewDocumentRequest = (text) => {
    const newDocIndicators = [
      'new document',
      'another document',
      'different document',
      'create a',
      'prepare a',
      'help me with a',
      'start a',
      'make a',
      'draft a'
    ];
    
    const normalizedText = text.toLowerCase();
    return newDocIndicators.some(indicator => normalizedText.includes(indicator));
  };
  
  // Helper function to detect if user wants to edit existing document
  const isEditingRequest = (text) => {
    const editIndicators = [
      'edit my',
      'change my',
      'update my',
      'modify my',
      'edit the',
      'change the',
      'update the',
      'modify the',
      'existing document',
      'I have a document',
      'my document',
      'add a clause',
      'remove a clause',
      'add provision',
      'change beneficiary'
    ];
    
    const normalizedText = text.toLowerCase();
    
    // Check for document key pattern
    const docKeyPattern = /[a-zA-Z]+-[a-f0-9]+-\d+-\d+\.docx/;
    if (docKeyPattern.test(text)) {
      return true;
    }
    
    return editIndicators.some(indicator => normalizedText.includes(indicator));
  };
  
  // Extract document key from user message
  const extractDocumentKey = (text) => {
    // Pattern for document keys like "trust-b716a27c-20250604-211059.docx"
    const docKeyPattern = /([a-zA-Z]+-[a-f0-9]+-\d+-\d+\.docx)/;
    const match = text.match(docKeyPattern);
    return match ? match[1] : null;
  };

  const downloadAllAsZip = async () => {
    try {
      console.log('Creating ZIP file with all documents...');
      
      // Show loading state
      const loadingDiv = document.createElement('div');
      loadingDiv.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg z-50';
      loadingDiv.textContent = 'Creating ZIP package...';
      document.body.appendChild(loadingDiv);
      
      // Create new ZIP instance
      const zip = new JSZip();
      
      // Get the correct API endpoint (use the same logic as at component level)
      const config = getEnvironmentConfig();
      const currentApiEndpoint = DOC_CREATION_API_BASE || 
        DOC_CREATION_API_BASE_IMPORT ||
        config.aws_cloud_logic_custom.find(api => api.name === 'DocCreationAPI')?.endpoint;
      
      if (!currentApiEndpoint) {
        console.error('Failed to get API endpoint for ZIP download');
        loadingDiv.remove();
        alert('Failed to determine API endpoint. Please try again.');
        return;
      }
      
      console.log('ZIP Download using API endpoint:', currentApiEndpoint);
      
      // Collect documents to download
      const documentsToDownload = [];
      
      if (isPackage) {
        // For packages, get all package URLs
        messages.forEach(msg => {
          if (msg.packageUrls) {
            Object.entries(msg.packageUrls).forEach(([docType, url]) => {
              const fullUrl = url.startsWith('/') ? `${currentApiEndpoint}${url}` : url;
              documentsToDownload.push({
                docType,
                url: fullUrl,
                filename: `${docType.replace(/\s+/g, '_')}.docx`
              });
            });
          }
        });
      } else {
        // For single document
        if (currentDocKey) {
          const docUrl = downloadDocument(currentDocKey, false);
          documentsToDownload.push({
            docType: currentDocType || 'Document',
            url: docUrl,
            filename: `${(currentDocType || 'Document').replace(/\s+/g, '_')}.docx`
          });
        }
      }
      
      if (documentsToDownload.length === 0) {
        loadingDiv.remove();
        alert('No documents to download');
        return;
      }
      
      // Update loading message
      loadingDiv.textContent = `Downloading ${documentsToDownload.length} documents...`;
      
      // Extract document keys from URLs
      const documentKeys = documentsToDownload.map(doc => {
        // Extract the key from the URL (everything after /download/)
        const match = doc.url.match(/\/download\/(.+)$/);
        return match ? match[1] : null;
      }).filter(key => key !== null);
      
      console.log('Document keys for batch download:', documentKeys);
      
      // Declare successCount at the proper scope
      let successCount = 0;
      
      // Use batch download endpoint
      try {
        const batchUrl = `${currentApiEndpoint}/download/batch`;
        console.log('Calling batch endpoint:', batchUrl);
        
        const batchResponse = await fetch(batchUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            documents: documentKeys
          })
        });
        
        if (!batchResponse.ok) {
          throw new Error(`Batch download failed: ${batchResponse.status} ${batchResponse.statusText}`);
        }
        
        const batchData = await batchResponse.json();
        console.log('Batch download response:', batchData);
        
        // Add successfully downloaded documents to ZIP
        
        // Handle the response format from your Lambda
        if (batchData.documents) {
          for (const [filename, docData] of Object.entries(batchData.documents)) {
            try {
              // Decode base64 content
              const binaryString = atob(docData.content);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              // Add to ZIP
              zip.file(filename, bytes);
              successCount++;
              loadingDiv.textContent = `Processing ${successCount}/${documentsToDownload.length} documents...`;
            } catch (err) {
              console.error(`Failed to add ${filename} to ZIP:`, err);
            }
          }
        }
        
        if (successCount === 0) {
          loadingDiv.remove();
          alert('No files could be added to ZIP');
          return;
        }
        
        // Show message if some files failed
        if (batchData.errors && batchData.errors.length > 0) {
          console.error('Batch download errors:', batchData.errors);
          const errorMsg = batchData.errors.map(e => `${e.key}: ${e.error}`).join('\n');
          alert(`ZIP created with ${successCount} documents. ${batchData.errors.length} files could not be included:\n${errorMsg}`);
        }
        
      } catch (error) {
        console.error('Batch download error:', error);
        loadingDiv.remove();
        alert(`Failed to download documents: ${error.message}`);
        return;
      }
      
      // Add README file
      const readmeContent = `Estate Planning Document Package
Generated: ${new Date().toLocaleDateString()}
Documents: ${documentsToDownload.length}

This package contains:
${documentsToDownload.map(d => `- ${d.filename}`).join('\n')}

IMPORTANT: These documents expire in 24 hours. 
Please save them to your document management system immediately.

Generated by TrustIQ
`;
      
      zip.file('README.txt', readmeContent);
      
      // Generate the ZIP file
      loadingDiv.textContent = 'Creating ZIP file...';
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });
      
      // Create filename with date
      const date = new Date().toISOString().split('T')[0];
      const clientName = 'Estate_Documents'; // You could extract this from variables
      const zipFilename = `${clientName}_${date}.zip`;
      
      // Download the ZIP
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      // Success message
      loadingDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
      loadingDiv.textContent = `✓ ZIP downloaded successfully!`;
      setTimeout(() => loadingDiv.remove(), 3000);
      
      console.log(`Successfully created ZIP with ${successCount} documents`);
      
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Failed to create ZIP file. Please try downloading documents individually.');
    }
  };

  const handleSend = async () => {
    if (!userInput.trim()) return;

    const userMessageText = userInput.trim();
    addMessage('user', userMessageText);
    setUserInput('');
    setIsLoading(true);

    console.log('=== HANDLE SEND STATE CHECK ===');
    console.log('States at start:', {
      currentDocType,
      currentDocKey,
      isCollectingPlaceholders,
      draftingInProgress,
      awaitingInfo
    });
    console.log('User message:', userMessageText);
    console.log('==============================');

    try {
      // Check if this is an editing request first
      if (isEditingRequest(userMessageText) && !workflowMode) {
        console.log("Editing request detected");
        setWorkflowMode('edit');
        
        // Try to extract document key from message
        const docKey = extractDocumentKey(userMessageText);
        
        if (docKey) {
          console.log("Document key found:", docKey);
          setEditingDocKey(docKey);

          // Ensure we have a session ID
          const activeSessionId = await ensureSessionId();

          // Call AI drafting agent directly (uses dedicated AI drafting agent MNDHV7IACN)
          const aiDraftResponse = await callAiDraftingAgent({
            docKey: docKey,
            prompt: userMessageText,
            sessionId: activeSessionId,
            docType: docKey.split('-')[0], // Extract doc type from key
            userSub: userInfo?.sub,
            firmId: userInfo?.firmId || 'default'
          });
          
          console.log("AI Draft Response received:", aiDraftResponse);
          
          // Handle successful instruction response FIRST
          if (aiDraftResponse.instructions && aiDraftResponse.instructions.length > 0) {
            const instruction = aiDraftResponse.instructions[0];
            
            // Check if this is a NEED_MORE_INFO action
            if (instruction.action === 'NEED_MORE_INFO') {
              addMessage('assistant', instruction.reason || 'I need more information to complete your request. Please provide more details.');
              return;
            }
            
            // Show what's being added
            let changeMessage = `I'll add the following to your document:\n\n`;
            changeMessage += `"${instruction.content || instruction.text || ''}"\n\n`;
            // Format article location - convert "article-xxii" to "Article XXII"
            let locationText = 'Appropriate section';
            if (instruction.articleId) {
              const articleNumber = instruction.articleId.replace('article-', '').toUpperCase();
              locationText = `Article ${articleNumber}`;
              if (instruction.articleTitle) {
                locationText += `, ${instruction.articleTitle}`;
              }
            } else if (instruction.position?.parentHeading) {
              locationText = instruction.position.parentHeading;
            }
            changeMessage += `Location: ${locationText}\n\n`;
            changeMessage += `✅ Your document has been updated successfully!`;
            
            addMessage('assistant', changeMessage);
            
            // Update document URL if provided
            if (aiDraftResponse.docUrl) {
              console.log("Setting document URL:", aiDraftResponse.docUrl);
              const newDocKey = aiDraftResponse.processedDocumentKey || aiDraftResponse.docKey || docKey;
              if (setDocKey) setDocKey(newDocKey);
              if (setDocUrl) setDocUrl(aiDraftResponse.docUrl);
            }
            
            // Auto-refresh document content after changes
            try {
              const refreshKey = aiDraftResponse.processedDocumentKey || aiDraftResponse.docKey || docKey;
              const resp = await viewDocumentAsHtml(refreshKey);
              if (resp?.html && setDocContent) {
                setDocContent(resp.html);
              }
            } catch (err) {
              console.error("Error refreshing document after edit:", err);
            }
            
            setDraftingInProgress(false);
            setAwaitingInfo(null);
          }
          // Handle NEED_MORE_INFO response
          else if (aiDraftResponse.needMoreInfo || aiDraftResponse.needMoreInfoReason) {
            const message = aiDraftResponse.assistantMessage || 
                           aiDraftResponse.needMoreInfoReason || 
                           aiDraftResponse.message ||
                           "Please specify the changes you want to make to the document.";
            addMessage('assistant', message);
            setAwaitingInfo('document changes');
            setDraftingInProgress(true);
            setIsLoading(false);
            return;
          }
          // Handle other responses
          else if (aiDraftResponse.assistantMessage || aiDraftResponse.message) {
            const message = aiDraftResponse.assistantMessage || aiDraftResponse.message;
            addMessage('assistant', message);
          }
          else {
            // This should rarely happen now
            addMessage('assistant', 
              "I encountered an issue processing your request. Please try again or provide more details."
            );
          }
          
          setIsLoading(false);
          return;
        } else {
          // No document key, ask for it
          addMessage('assistant', 
            "I see you want to edit a document. Please provide the document name or key (like 'trust-abc123-20250101-123456.docx'), or describe which document you'd like to edit."
          );
          setIsLoading(false);
          return;
        }
      }
      
      // If already in edit mode, continue editing
      if (workflowMode === 'edit' && editingDocKey) {
        console.log("Continuing edit workflow for:", editingDocKey);

        // Ensure we have a session ID
        const activeSessionId = await ensureSessionId();

        const aiDraftResponse = await callAiDraftingAgent({
          docKey: editingDocKey,
          prompt: userMessageText,
          sessionId: activeSessionId,
          docType: editingDocKey.split('-')[0],
          userSub: userInfo?.sub,
          firmId: userInfo?.firmId || 'default'
        });
        
        console.log("Continuing edit response:", aiDraftResponse);
        
        // Handle successful instruction response FIRST
        if (aiDraftResponse.instructions && aiDraftResponse.instructions.length > 0) {
          const instruction = aiDraftResponse.instructions[0];
          
          // Check if this is a NEED_MORE_INFO action
          if (instruction.action === 'NEED_MORE_INFO') {
            addMessage('assistant', instruction.reason || 'I need more information to complete your request. Please provide more details.');
            return;
          }
          
          let changeMessage = `I've made the following changes to your document:\n\n`;
          changeMessage += `"${instruction.content || instruction.text || ''}"\n\n`;
          // Format article location - convert "article-xxii" to "Article XXII"
          let locationText = 'Appropriate section';
          if (instruction.articleId) {
            const articleNumber = instruction.articleId.replace('article-', '').toUpperCase();
            locationText = `Article ${articleNumber}`;
            if (instruction.articleTitle) {
              locationText += `, ${instruction.articleTitle}`;
            }
          } else if (instruction.position?.parentHeading) {
            locationText = instruction.position.parentHeading;
          }
          changeMessage += `Location: ${locationText}\n\n`;
          changeMessage += `✅ Your document has been updated successfully!`;
          
          addMessage('assistant', changeMessage);
          
          // Update document URL
          if (aiDraftResponse.docUrl) {
            const newDocKey = aiDraftResponse.processedDocumentKey || editingDocKey;
            if (setDocKey) setDocKey(newDocKey);
            if (setDocUrl) setDocUrl(aiDraftResponse.docUrl);
          }
          
          // Auto-refresh document content after changes
          try {
            const refreshKey = aiDraftResponse.processedDocumentKey || aiDraftResponse.docKey || docKey;
            const resp = await viewDocumentAsHtml(refreshKey);
            if (resp?.html && setDocContent) {
              setDocContent(resp.html);
            }
          } catch (err) {
            console.error("Error refreshing document after edit:", err);
          }
          
          setDraftingInProgress(false);
          setAwaitingInfo(null);
        }
        // Handle NEED_MORE_INFO response
        else if (aiDraftResponse.needMoreInfo || aiDraftResponse.needMoreInfoReason) {
          const message = aiDraftResponse.assistantMessage || 
                         aiDraftResponse.needMoreInfoReason || 
                         aiDraftResponse.message ||
                         "Please provide more details about what you'd like to change.";
          addMessage('assistant', message);
          setAwaitingInfo('details about changes');
          setIsLoading(false);
          return;
        }
        // Handle other assistant messages
        else if (aiDraftResponse.assistantMessage || aiDraftResponse.message) {
          const message = aiDraftResponse.assistantMessage || aiDraftResponse.message;
          addMessage('assistant', message);
        } else {
          // This should rarely happen now
          addMessage('assistant', 
            "I received your request. What specific changes would you like to make?"
          );
        }
        
        setIsLoading(false);
        return;
      }
      
      // If we haven't set a doc type yet, detect it now
      // and use a local variable to avoid the async setState timing issue.
      let docTypeToUse = currentDocType;
      
      // Check if user is providing CSV data (don't detect doc type from CSV content)
      const isProvidingCSVData = isCollectingPlaceholders && 
        (userMessageText.includes('\t') || 
         (userMessageText.includes(',') && userMessageText.split('\n').length > 5));
      
      // Check if user is requesting a new document (whether or not one exists)
      // Don't detect document type if they're providing CSV data
      const newDocTypeDetected = !isProvidingCSVData ? detectDocumentType(userMessageText) : null;
      const wantsNewDocument = isNewDocumentRequest(userMessageText);

      // Reset if:
      // 1. We have a current doc type but user wants a different one
      // 2. User explicitly wants a new document
      // 3. We're in placeholder collection phase and user requests a new doc type
      if ((currentDocType && newDocTypeDetected && newDocTypeDetected !== currentDocType) || 
          (currentDocType && wantsNewDocument) ||
          (isCollectingPlaceholders && newDocTypeDetected)) {
        
        console.log("New document request detected, resetting for new document");
        console.log("- Current doc type:", currentDocType);
        console.log("- New doc type detected:", newDocTypeDetected);
        console.log("- Is collecting placeholders:", isCollectingPlaceholders);
        
        resetForNewDocument();
        docTypeToUse = newDocTypeDetected;
        setCurrentDocType(newDocTypeDetected);
        
        // Update package status
        const isPackageRequest = 
          newDocTypeDetected === 'Disclaimer Package' || 
          (newDocTypeDetected && newDocTypeDetected.toLowerCase().includes('package'));
        if (setIsPackage) {
          setIsPackage(isPackageRequest);
        }
      }
      
      if (!currentDocType && !docTypeToUse) {
        const detectedType = detectDocumentType(userMessageText);
        if (detectedType) {
          docTypeToUse = detectedType;
          setCurrentDocType(detectedType); 
          console.log("Document type detected:", detectedType);
          
          // Check if this is a package request
          const isPackageRequest = 
            detectedType === 'Disclaimer Package' || 
            detectedType.toLowerCase().includes('package');
          
          if (setIsPackage) {
            setIsPackage(isPackageRequest);
          }
        } else {
          // If no type detected, don't default to 'will'
          // Instead, let the backend handle it or ask the user for clarification
          console.log("No document type detected, will ask user for clarification");
          addMessage('assistant', "I'd be happy to help you create a document. Could you please specify which type of document you'd like to create? I can help with:\n\n• Wills\n• Trusts\n• Powers of Attorney (Financial or Healthcare)\n• HIPAA Authorizations\n• Advance Directives\n• Complete Estate Planning Packages\n\nWhat would you like to start with?");
          setIsLoading(false);
          return;
        }
      }

      // Check if user is providing requested info or requesting document editing
      const isProvidingRequestedInfo = awaitingInfo &&
        (userMessageText.toLowerCase().includes(awaitingInfo.toLowerCase()) ||
         userMessageText.toLowerCase().includes("name"));

      // Make isDraftingRequest more specific - don't treat new doc requests as drafting
      const isDraftingRequest = currentDocKey && (
        draftingInProgress ||
        isProvidingRequestedInfo ||
        (/(draft|revise|edit|update|modify|change|rewrite|help me (with|add|edit|update)|add( to)?|improve|fix|amend|bequeath|leave|give|gift)/i.test(userMessageText) &&
         !newDocTypeDetected) // Don't treat as drafting if it's a new doc type
      );

      // Check for "leave placeholders blank" instruction
      const isSkippingPlaceholders = 
        userMessageText.toLowerCase().includes('leave') && 
        userMessageText.toLowerCase().includes('placeholder') && 
        userMessageText.toLowerCase().includes('blank');

      // DOCUMENT CREATION FLOW
      if (!currentDocKey && docTypeToUse) {
        // Check if user is providing complete CSV data (for ANY document type)
        const csvVariables = detectAndParseCSVData(userMessageText);
        const isCompleteDataSubmission = csvVariables && Object.keys(csvVariables).length >= 5; // Reasonable threshold
        
        if (isCompleteDataSubmission) {
          // User has provided complete CSV data - use direct generation for BOTH packages AND single documents
          console.log('=== DIRECT GENERATION FLOW ===');
          console.log('Document type:', docTypeToUse);
          console.log('Is package:', isPackage);
          console.log('Variables provided:', Object.keys(csvVariables).length);
          console.log('Using Lambda Function URL exclusively (15 min timeout)');
          
          // Set progress tracking (adjust timing for single vs package)
          const expectedTime = isPackage || docTypeToUse === 'Disclaimer Package' ? 75 : 30;
          setGenerationProgress({
            startTime: Date.now(),
            documentType: docTypeToUse
          });
          
          const isPackageDoc = docTypeToUse === 'Disclaimer Package' || docTypeToUse.toLowerCase().includes('package');
          addMessage('system', `Processing your complete placeholder data. Generating ${isPackageDoc ? '5 documents (Will, Trust, Financial POA, Healthcare POA, HIPAA)' : 'your document'}...`);
          
          try {
            // Prepare the request data
            // Ensure we have a session ID
            const activeSessionId = await ensureSessionId();

            let requestData = {
              documentType: docTypeToUse,
              variables: csvVariables,
              userMessage: userMessageText,
              isPackage: isPackageDoc,
              placeholders: Object.keys(csvVariables),
              docMetadata: {},
              sessionId: activeSessionId,
              userSub: userInfo?.sub,
              firmId: userInfo?.firmId || 'default'
            };

            // Add package-specific data
            if (isPackageDoc) {
              if (docTypeToUse === 'Disclaimer Package') {
                requestData.packageDocs = ["Will", "Trust", "Financial Power of Attorney", "Healthcare Power of Attorney", "HIPAA Authorization"];
                requestData.packageTemplatePaths = {
                  "Will": "wills/will-pourover.docx",
                  "Trust": "trusts/trust-disclaimer.docx",
                  "Financial Power of Attorney": "powers/power-of-attorney-financial.docx",
                  "Healthcare Power of Attorney": "powers/power-of-attorney-healthcare.docx",
                  "HIPAA Authorization": "authorizations/HIPAA.docx"
                };
                requestData.templatePath = "";
              } else if (docTypeToUse === 'Healthcare Documents Package') {
                requestData.packageDocs = ["Healthcare Power of Attorney", "HIPAA Authorization"];
                requestData.packageTemplatePaths = {
                  "Healthcare Power of Attorney": "powers/power-of-attorney-healthcare.docx",
                  "HIPAA Authorization": "authorizations/HIPAA.docx"
                };
                requestData.templatePath = "";
              }
            } else {
              // Single document - determine template path
              const templateMappings = {
                'will': 'wills/will-pourover.docx',
                'trust': 'trusts/trust-disclaimer.docx',
                'financial power of attorney': 'powers/power-of-attorney-financial.docx',
                'healthcare power of attorney': 'powers/power-of-attorney-healthcare.docx',
                'hipaa authorization': 'authorizations/HIPAA.docx',
                'power of attorney': 'powers/power-of-attorney-general.docx',
                'advance directive': 'advance-directives/advance-directive.docx',
                'living will': 'advance-directives/living-will.docx'
              };
              
              const docTypeLower = docTypeToUse.toLowerCase();
              let templatePath = '';
              for (const [key, path] of Object.entries(templateMappings)) {
                if (docTypeLower.includes(key)) {
                  templatePath = path;
                  break;
                }
              }
              
              requestData.templatePath = templatePath;
              console.log('Single document template path:', templatePath);
            }
            
            // Call the direct generation endpoint
            console.log('Calling generateDocumentsDirect with:', requestData);
            const directResponse = await generateDocumentsDirect(requestData);
            
            console.log('Direct generation response received:', directResponse);
            
            // Clear progress when done
            setGenerationProgress(null);
            
            if (directResponse.success && directResponse.status === 'completed') {
              // Check both possible URL fields
              const urls = directResponse.documentUrls || directResponse.packageUrls;
              
              if (urls && Object.keys(urls).length > 0) {
                // Package response
                console.log('Successfully received', Object.keys(urls).length, 'document URLs');
                
                if (setPackageUrls) {
                  setPackageUrls(urls);
                }
                
                // Get first document key for the viewer
                const firstUrl = Object.values(urls)[0];
                const docKey = getDocumentKey(firstUrl);
                
                if (setDocKey) setDocKey(docKey);
                if (onDocumentCreated) {
                  onDocumentCreated(docKey, true, urls);
                }
                
                // Format message with all URLs
                const urlList = Object.entries(urls)
                  .map(([docType, url]) => `- ${docType}: ${url}`)
                  .join('\n');
                
                addMessage('assistant', `✅ Your documents have been generated successfully!\n\nHere are your documents:\n\n${urlList}\n\nWould you like to:\n• Edit any of these documents (just tell me what changes you'd like)\n• Create a new document (click "Start New Document" below)\n• Download your documents (use the download buttons below)`, null, urls);
                
              } else if (directResponse.documentUrl) {
                // Single document response
                const docUrl = directResponse.documentUrl;
                const docKey = getDocumentKey(docUrl);
                
                console.log('Single document generated successfully:');
                console.log('- Document URL:', docUrl);
                console.log('- Document Key:', docKey);
                
                if (setDocKey) setDocKey(docKey);
                if (setDocUrl) {
                  console.log('Setting docUrl in parent component:', docUrl);
                  setDocUrl(docUrl);  // This should now work!
                }
                if (onDocumentCreated) {
                  onDocumentCreated(docKey, false);
                }
                
                addMessage('assistant', 
                  `✅ Your ${docTypeToUse} has been generated successfully!\n\n` +
                  `Document URL: ${docUrl}\n\n` +
                  `Would you like to:\n` +
                  `• Edit this document (just tell me what changes you'd like)\n` +
                  `• Create a new document (click "Start New Document" below)\n` +
                  `• Download your document (use the download button below)`, 
                  docUrl
                );
              } else {
                console.error('No URLs found in successful response:', directResponse);
                addMessage('system', 'Documents were generated but URLs were not returned. Please refresh and try again.');
              }
            } else if (directResponse.jobId && directResponse.status === 'pending') {
              // Job-based processing - poll for status
              addMessage('system', 'Your documents are being processed. This may take a moment...');
              
              // Start polling for job status
              const pollInterval = setInterval(async () => {
                try {
                  const statusResponse = await checkJobStatus(directResponse.jobId);
                  console.log('Job status:', statusResponse);
                  
                  if (statusResponse.status === 'completed' && statusResponse.documentUrls) {
                    clearInterval(pollInterval);
                    setGenerationProgress(null);
                    
                    const urls = statusResponse.documentUrls;
                    console.log('Job completed with URLs:', urls);
                    
                    if (setPackageUrls) {
                      setPackageUrls(urls);
                    }
                    
                    // Get first document key for the viewer
                    const firstUrl = Object.values(urls)[0];
                    const docKey = getDocumentKey(firstUrl);
                    
                    if (setDocKey) setDocKey(docKey);
                    if (onDocumentCreated) {
                      onDocumentCreated(docKey, true, urls);
                    }
                    
                    // Format message with all URLs
                    const urlList = Object.entries(urls)
                      .map(([docType, url]) => `- ${docType}: ${url}`)
                      .join('\n');
                    
                    addMessage('assistant', `✅ Your documents have been generated successfully!\n\nHere are your documents:\n\n${urlList}\n\nWould you like to:\n• Edit any of these documents (just tell me what changes you'd like)\n• Create a new document (click "Start New Document" below)\n• Download your documents (use the download buttons below)`, null, urls);
                  } else if (statusResponse.status === 'failed' || statusResponse.error) {
                    clearInterval(pollInterval);
                    setGenerationProgress(null);
                    addMessage('system', `Document generation failed: ${statusResponse.error || 'Unknown error'}`);
                  }
                } catch (pollError) {
                  console.error('Error polling job status:', pollError);
                }
              }, 3000); // Poll every 3 seconds
              
              // Stop polling after 2 minutes
              setTimeout(() => {
                clearInterval(pollInterval);
                setGenerationProgress(null);
              }, 120000);
              
            } else {
              // Generation failed
              console.error('Document generation failed:', directResponse);
              
              // Handle package-specific errors
              if (directResponse.isPackageError) {
                addMessage('system', directResponse.error);
              } else if (directResponse.requiresConfiguration) {
                addMessage('system', directResponse.error);
              } else if (directResponse.isTimeout && !isPackage) {
                // Only show timeout message for single documents
                addMessage('system', directResponse.error);
              } else {
                addMessage('system', `Document generation failed: ${directResponse.error || 'Unknown error'}`);
              }
            }
            
          } catch (directErr) {
            console.error('Error with direct generation:', directErr);
            setGenerationProgress(null);
            
            // Handle package-specific errors differently
            if (isPackageDoc) {
              // For packages, don't mention API Gateway timeouts
              addMessage('system', `Package generation error: ${directErr.message || 'Unknown error occurred'}`);
            } else {
              // For single documents, handle timeout normally
              if (directErr.code === 'ECONNABORTED' || directErr.message?.includes('timeout')) {
                addMessage('system', 
                  'The request timed out at the API Gateway (29 second limit).'
                );
              } else {
                addMessage('system', `Error generating document: ${directErr.message || 'Unknown error'}`);
              }
            }
          }
          
          setIsLoading(false);
          return; // Exit early, we're done!
        }
        
        // Only use orchestration when we DON'T have complete data (for placeholder collection)
        if (!isCompleteDataSubmission) {
          console.log('=== ORCHESTRATION FLOW: PLACEHOLDER COLLECTION PHASE ===');
          console.log('Document type:', docTypeToUse);
          console.log('Is package:', isPackage);
          console.log('Using API Gateway for placeholder collection (29s timeout is OK here)');
          
          // Check if this is placeholder collection phase or document generation phase
          const isProvidingPlaceholderData = isCollectingPlaceholders && 
            (userMessageText.includes('\t') || userMessageText.includes(','));
          
          addMessage('system', `Starting ${isPackage ? 'document package' : 'document'} creation process...`);

        // Log to verify document type is preserved
        console.log('=== ORCHESTRATION CALL PARAMS ===');
        console.log('Document type being sent:', docTypeToUse);
        console.log('Is collecting placeholders:', isCollectingPlaceholders);
        console.log('Is providing CSV data:', isProvidingCSVData);
        console.log('Current isPackage state:', isPackage);
        console.log('================================');

        // Ensure we have a session ID
        const activeSessionId = await ensureSessionId();

        // Use docTypeToUse so we don't fallback to "will" if we already detected a type
        const orchResponse = await orchestrateDocument(
          userMessageText,
          docTypeToUse,
          activeSessionId,
          null,
          isSkippingPlaceholders,
          userInfo?.sub,
          userInfo?.firmId || 'default'
        );

        console.log('=== FULL ORCHESTRATION RESPONSE ===');
        console.log('Raw orchResponse:', orchResponse);
        console.log('orchResponse.documentKey:', orchResponse?.documentKey);
        console.log('orchResponse.documentUrl:', orchResponse?.documentUrl);
        console.log('orchResponse.docUrl:', orchResponse?.docUrl);
        console.log('orchResponse.packageUrls:', orchResponse?.packageUrls);
        console.log('orchResponse.error:', orchResponse?.error);
        console.log('orchResponse.assistantMessage exists?', !!orchResponse?.assistantMessage);
        console.log('orchResponse keys:', Object.keys(orchResponse || {}));
        console.log('===================================');

        // FIXED: Check for error response first
        if (orchResponse?.error) {
          console.log('❌ Error in orchestration response:', orchResponse.error);
          addMessage('system', `Error: ${orchResponse.error}`);
          setIsLoading(false);
          return;
        }

        // FIXED: Handle the placeholder collection phase (no documentKey yet)
        if (!orchResponse?.documentKey && orchResponse?.assistantMessage) {
          setIsCollectingPlaceholders(true);
          addMessage('assistant', orchResponse.assistantMessage);
          
          // Check if we're waiting for specific info
          if (orchResponse.missingPlaceholders && orchResponse.missingPlaceholders.length > 0) {
            setAwaitingInfo(orchResponse.missingPlaceholders[0]);
          }
          
          setIsLoading(false);
          return;
        }

        // Document creation phase (documentKey exists)
        if (orchResponse?.documentKey) {
          setIsCollectingPlaceholders(false);
          
          // Save the document key
          if (setDocKey) setDocKey(orchResponse.documentKey);
          
          // Process package URLs if available
          let pkgUrls = null;
          let firstDocUrl = null;
          let extractedDocUrl = null;
          
          if (orchResponse.packageUrls) {
            console.log("5. Package URLs received:", orchResponse.packageUrls);
            pkgUrls = orchResponse.packageUrls;
            
            // Update parent component with package URLs
            if (setPackageUrls) {
              setPackageUrls(orchResponse.packageUrls);
            }
            
            // Get the first document URL for the viewer
            const urlValues = Object.values(orchResponse.packageUrls);
            if (urlValues.length > 0) {
              firstDocUrl = urlValues[0];
              console.log("Setting first document URL for viewer:", firstDocUrl);
              if (setDocUrl) {
                setDocUrl(firstDocUrl);
              }
            }
          } else if (orchResponse.documentUrl) {
            // Single document case - URL provided
            console.log("5. Found documentUrl in response:", orchResponse.documentUrl);
            extractedDocUrl = orchResponse.documentUrl;
          } else {
            // Try to extract URL from the assistant message
            const assistantMsg = orchResponse.assistantMessage || '';
            console.log('6. Searching for URL in message:', assistantMsg);
            
            // Improved regex to capture the document key more reliably
            const urlMatch = assistantMsg.match(/\/download\/([^\s]+\.docx)/);
            console.log('7. URL match result:', urlMatch);
            
            if (urlMatch && urlMatch[0] && !orchResponse.packageUrls) {
              // Found a URL in the message for a single document
              extractedDocUrl = urlMatch[0];
              console.log("8. Extracted full URL from message:", extractedDocUrl);
              
              // Also extract just the document key if needed
              if (urlMatch[1] && !orchResponse.documentKey) {
                const extractedKey = urlMatch[1];
                console.log("8a. Extracted document key:", extractedKey);
                if (setDocKey) {
                  setDocKey(extractedKey);
                }
              }
            } else if (!orchResponse.packageUrls && orchResponse.documentKey) {
              // Single document case - construct URL from documentKey as fallback
              extractedDocUrl = `/download/${orchResponse.documentKey}`;
              console.log("9. Constructed URL from key:", extractedDocUrl);
            }
          }
          
          console.log('10. Final extractedDocUrl:', extractedDocUrl);
          console.log('11. setDocUrl function exists?', !!setDocUrl);
          console.log('12. Final docUrl state check before setting:', extractedDocUrl);
          
          // Set the URL immediately if we have it
          if (extractedDocUrl && setDocUrl) {
            console.log('13. Calling setDocUrl with:', extractedDocUrl);
            setDocUrl(extractedDocUrl);
            
            // Also verify it was set
            setTimeout(() => {
              console.log('14. Verification - docUrl should now be:', extractedDocUrl);
            }, 100);
          } else if (!extractedDocUrl) {
            console.warn('15. WARNING: No document URL could be extracted or constructed!');
          }
          
          console.log('=== END DOCUMENT URL DEBUGGING ===');
          
          // Call onDocumentCreated callback if available - pass isPackage and packageUrls
          const isPackageDoc = 
            docTypeToUse === 'Disclaimer Package' || 
            docTypeToUse.toLowerCase().includes('package');
            
          if (onDocumentCreated) {
            onDocumentCreated(orchResponse.documentKey, isPackageDoc, pkgUrls);
          }
          
          // Add message from the assistant
          const assistantMsg = orchResponse.assistantMessage || (orchResponse?.packageUrls ? "Your document package has been prepared." : "Your document has been prepared and is ready to view.");
          console.log('Assistant message to display:', assistantMsg);
          console.log('Assistant message length:', assistantMsg.length);
          console.log('Contains CSV?', assistantMsg.includes('---- Copy from below this line ----'));
          
          console.log('🔴 About to call addMessage with:');
          console.log('  - assistantMsg:', assistantMsg?.substring(0, 100) + '...');
          console.log('  - extractedDocUrl:', extractedDocUrl);
          console.log('  - isPackage:', orchResponse?.packageUrls ? 'Yes' : 'No');
          
          if (orchResponse?.packageUrls) {
            // Add this line to include packageUrls in the message
            addMessage('assistant', assistantMsg, null, orchResponse.packageUrls);
          } else {
            // Normal case for non-package documents - pass the extracted URL
            console.log('🔴 Calling addMessage with extractedDocUrl:', extractedDocUrl);
            addMessage('assistant', assistantMsg, extractedDocUrl);
          }
          
          // Try to load the HTML content for the document viewer
          try {
            const htmlContent = await viewDocumentAsHtml(orchResponse.documentKey);
            if (htmlContent?.html && setDocContent) {
              setDocContent(htmlContent.html);
            }
            
            // Document URL will be set via onDocumentCreated callback
            // which is called above at line 529
          } catch (htmlErr) {
            console.error("Error loading document content:", htmlErr);
            addMessage('system', "Could not load document content. Please try refreshing.");
          }

        } else {
          // Generic fallback message
          console.log('⚠️ Unexpected response format - no documentKey or assistantMessage');
          addMessage(
            'assistant',
            "I'm working on preparing your document. What specific information would you like to include?"
          );
        }
        } // End of orchestration flow (!isCompleteDataSubmission)
      }
      
      // DOCUMENT EDITING/DRAFTING FLOW
      else if (isDraftingRequest && currentDocKey) {
        addMessage('system', `Processing your drafting request...`);

        // Ensure we have a session ID
        const activeSessionId = await ensureSessionId();

        // Pass docTypeToUse if possible
        const draftResponse = await callAiDraftingAgent({
          docKey: currentDocKey,
          prompt: userMessageText,
          sessionId: activeSessionId,
          docType: currentDocType || "Will",  // Changed default to Title Case
          userSub: userInfo?.sub,
          firmId: userInfo?.firmId || 'default'
        });

        console.log("Draft response:", draftResponse);

        if (draftResponse?.error) {
          throw new Error(draftResponse.error);
        }

        // Handle need for more information
        if (draftResponse?.needMoreInfo) {
          addMessage('assistant', `I need more information to complete your request: ${draftResponse.needMoreInfoReason || 'Please provide more details'}`);
          setAwaitingInfo(draftResponse.needMoreInfoReason || 'additional information');
          setDraftingInProgress(true);
          setIsLoading(false);
          return;
        }

        // Handle successful instruction response FIRST (before checking for needMoreInfo)
        if (draftResponse?.instructions && draftResponse.instructions.length > 0) {
          const instruction = draftResponse.instructions[0];
          
          // Check if this is a NEED_MORE_INFO action
          if (instruction.action === 'NEED_MORE_INFO') {
            addMessage('assistant', instruction.reason || 'I need more information to complete your request. Please provide more details.');
            setIsLoading(false);
            return;
          }
          
          // Display the actual changes that were made
          let changesMessage = `I've made the following changes to your document:\n\n`;
          changesMessage += `"${instruction.content || instruction.text || ''}"\n\n`;
          if (instruction.position?.parentHeading) {
            // Format article location - convert "article-xxii" to "Article XXII"
            let locationText = 'Appropriate section';
            if (instruction.articleId) {
              const articleNumber = instruction.articleId.replace('article-', '').toUpperCase();
              locationText = `Article ${articleNumber}`;
              if (instruction.articleTitle) {
                locationText += `, ${instruction.articleTitle}`;
              }
            } else if (instruction.position?.parentHeading) {
              locationText = instruction.position.parentHeading;
            }
            changesMessage += `Location: ${locationText}\n\n`;
          }
          changesMessage += `✅ Your document has been updated successfully. You can download the edited version below.`;
          
          addMessage('assistant', changesMessage);
          
          // Update document URL if provided
          if (draftResponse.docUrl) {
            console.log("Updating document URL to:", draftResponse.docUrl);
            if (setDocUrl) setDocUrl(draftResponse.docUrl);
            const newDocKey = draftResponse.processedDocumentKey || draftResponse.docKey || currentDocKey;
            if (setDocKey) setDocKey(newDocKey);
          }
          
          // Refresh document content
          try {
            const resp = await viewDocumentAsHtml(currentDocKey);
            if (resp?.html && setDocContent) {
              setDocContent(resp.html);
            }
          } catch (err) {
            console.error("Error updating document view:", err);
          }
          
          setDraftingInProgress(false);
          setAwaitingInfo(null);
          setIsLoading(false);
          return;
        }
        
        // Handle NEED_MORE_INFO response
        if (draftResponse?.needMoreInfo || draftResponse?.needMoreInfoReason) {
          const message = draftResponse.assistantMessage || 
                         draftResponse.needMoreInfoReason || 
                         draftResponse.message ||
                         "Please specify the changes you want to make to the document.";
          addMessage('assistant', message);
          setAwaitingInfo('document changes');
          setDraftingInProgress(true);
          setIsLoading(false);
          return;
        }
        
        // Handle assistant message responses
        if (draftResponse?.assistantMessage || draftResponse?.message) {
          const message = draftResponse.assistantMessage || draftResponse.message;
          addMessage('assistant', message);
          setDraftingInProgress(false);
          setAwaitingInfo(null);
          setIsLoading(false);
          return;
        }

        // Handle revised HTML content
        if (
          draftResponse?.revisedHtml &&
          typeof draftResponse.revisedHtml === 'string' &&
          draftResponse.revisedHtml.length > 10 &&
          !draftResponse.revisedHtml.includes('need_more_info')
        ) {
          addMessage('assistant', `Your changes have been applied to the document.`);
          setDraftingInProgress(false);
          setAwaitingInfo(null);

          // Call onDraftRevision callback if available
          if (onDraftRevision && draftResponse.articleId) {
            onDraftRevision({
              articleId: draftResponse.articleId,
              revisedHtml: draftResponse.revisedHtml
            });
          }

          // Refresh document content
          try {
            const resp = await viewDocumentAsHtml(currentDocKey);
            if (resp?.html && setDocContent) {
              setDocContent(resp.html);
            }
          } catch (err) {
            console.error("Error updating document view:", err);
          }
        }
        // Handle success message
        else if (draftResponse?.success) {
          addMessage('assistant', `Your changes have been stored and applied to the document.`);
          setDraftingInProgress(false);
          setAwaitingInfo(null);

          // Refresh document content
          try {
            const resp = await viewDocumentAsHtml(currentDocKey);
            if (resp?.html && setDocContent) {
              setDocContent(resp.html);
            }
          } catch (err) {
            console.error("Error updating document view:", err);
          }
        }
        // Handle unexpected response format
        else {
          console.warn("Unexpected drafting response format:", draftResponse);
          // Try to use any message from the response
          const fallbackMessage = draftResponse?.message || 
                                draftResponse?.error ||
                                "I received your request. Please provide more details about what you'd like to change in the document.";
          addMessage('assistant', fallbackMessage);
          setDraftingInProgress(true);
        }
      }
      
      // GENERAL CONVERSATION FLOW
      else {
        // For general questions or when we're not sure what to do
        addMessage(
          'assistant',
          `I can help you create and edit your estate planning document. 
           What specific changes would you like to make, or would you like me to help with something else?`
        );
      }
    } catch (error) {
      console.error("Error in chat interface:", error);
      addMessage('system', `An error occurred: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderMessageContent = (message) => {
    // Handle both cases: message object or string content
    const content = typeof message === 'string' ? message : message.content;
    
    console.log('=== renderMessageContent DEBUG ===');
    console.log('Message role:', message.role);
    console.log('Content length:', content?.length);
    console.log('Content preview:', content?.substring(0, 200));
    console.log('Contains CSV delimiter?', content?.includes('---- Copy the table below to a spreadsheet ----'));
    console.log('Contains old delimiter?', content?.includes('---- Copy from below this line ----'));
    
    if (content?.includes('Your placeholders for the Trust have been identified')) {
      console.log('Found the expected message start!');
    }
    console.log('=== END renderMessageContent DEBUG ===');
    
    // Safety check for content
    if (!content) {
      console.error('renderMessageContent: content is null or undefined!');
      return <div className="text-red-500">Error: Message content is missing</div>;
    }
    
    // Check if message contains CSV table
    if (content.includes('---- Copy from below this line ----') || 
        content.includes('---- Copy the table below to a spreadsheet ----')) {
      
      // Find which delimiter is used
      const csvDelimiter = content.includes('---- Copy from below this line ----') 
        ? '---- Copy from below this line ----'
        : '---- Copy the table below to a spreadsheet ----';
      
      const parts = content.split(csvDelimiter);
      const beforeCSV = parts[0];
      
      // Handle the end delimiter if present
      const afterCSVParts = parts[1] ? parts[1].split('---- End of table ----') : ['', ''];
      const csvContent = afterCSVParts[0].trim();
      const afterCSV = afterCSVParts[1] || '';
      
      // Create CSV download functionality
      const downloadCSV = () => {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'disclaimer_package_placeholders.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      
      // Copy to clipboard functionality
      const copyToClipboard = () => {
        navigator.clipboard.writeText(csvContent).then(() => {
          // Show success notification
          const notification = document.createElement('div');
          notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
          notification.textContent = 'CSV copied to clipboard!';
          document.body.appendChild(notification);
          setTimeout(() => notification.remove(), 3000);
        }).catch(err => {
          console.error('Failed to copy:', err);
          alert('Failed to copy to clipboard. Please try selecting and copying manually.');
        });
      };
      
      return (
        <div>
          <div className="whitespace-pre-wrap">{beforeCSV}</div>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-semibold text-gray-700">Placeholder Information Table</h4>
              <div className="space-x-2">
                <button
                  onClick={downloadCSV}
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                >
                  Download CSV
                </button>
                <button
                  onClick={copyToClipboard}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto bg-white p-2 border border-gray-300 rounded">
              <pre className="text-xs whitespace-pre font-mono">{csvContent}</pre>
            </div>
            <div className="mt-2 text-xs text-gray-600">
              Tip: Click "Copy to Clipboard" then paste directly into Excel or Google Sheets
            </div>
          </div>
          {afterCSV && (
            <div className="mt-4 whitespace-pre-wrap">{afterCSV}</div>
          )}
        </div>
      );
    }
    
    // Handle packageUrls if present in the message
    if (message.packageUrls && typeof message === 'object') {
      return (
        <div>
          <div className="whitespace-pre-wrap mb-4">{content}</div>
          <div className="space-y-2">
            {Object.entries(message.packageUrls).map(([docType, url]) => {
              const fullUrl = url.startsWith('/') ? `${DOC_CREATION_API_BASE}${url}` : url;
              return (
                <a
                  key={docType}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download {docType}
                </a>
              );
            })}
          </div>
        </div>
      );
    }
    
    // Handle single document URL if present in the message
    if (message.docUrl && typeof message === 'object') {
      const fullUrl = message.docUrl.startsWith('/') ? `${DOC_CREATION_API_BASE}${message.docUrl}` : message.docUrl;
      return (
        <div>
          <div className="whitespace-pre-wrap mb-4">{content}</div>
          <div className="space-y-2">
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Edited Document
            </a>
          </div>
        </div>
      );
    }
    
    return (
      <div className="whitespace-pre-wrap">{content}</div>
    );
  };

  const downloadCurrentDocument = async () => {
    if (currentDocKey) {
      try {
        console.log('Downloading document:', { currentDocKey, isPackage, sessionId });

        // Use authenticated download with sessionId
        const result = await downloadDocumentAuthenticated(currentDocKey, isPackage, sessionId);

        if (!result.success) {
          console.error('Download failed:', result.error);
          // Show error to user
          setMessages(prev => [...prev, {
            role: 'system',
            content: `Download failed: ${result.error}`
          }]);
        }
      } catch (error) {
        console.error('Error downloading document:', error);
        setMessages(prev => [...prev, {
          role: 'system',
          content: 'Failed to download document. Please try again.'
        }]);
      }
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-grow overflow-y-auto mb-4 space-y-4 p-4">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`p-3 rounded ${
              msg.role === 'assistant'
                ? 'bg-blue-50'
                : msg.role === 'system'
                ? 'bg-green-50'
                : 'bg-gray-50'
            }`}
          >
            <div className="font-bold mb-1">
              {msg.role === 'user'
                ? 'You'
                : msg.role === 'assistant'
                ? 'Assistant'
                : 'System'}
            </div>
            {renderMessageContent(msg)}
          </div>
        ))}
        {isLoading && generationProgress && (
          <GenerationProgress 
            startTime={generationProgress.startTime} 
            documentType={generationProgress.documentType}
          />
        )}
        {isLoading && !generationProgress && (
          <div className="text-gray-400 italic p-3">Assistant is typing...</div>
        )}
      </div>

      <div className="p-4 border-t bg-white">
        <div className="flex border rounded overflow-hidden">
          <textarea
            className="flex-grow p-2 resize-none"
            rows="2"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your request..."
            disabled={isLoading}
          />
          <button
            className={`px-4 py-2 ${
              isLoading || !userInput.trim()
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
            onClick={handleSend}
            disabled={isLoading || !userInput.trim()}
          >
            Send
          </button>
        </div>
        
        {/* Show reset button when in placeholder collection phase without a document */}
        {!currentDocKey && (currentDocType || isCollectingPlaceholders) && (
          <div className="mt-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-sm text-gray-700 mb-2">
              Currently preparing: <strong>{currentDocType || 'Document'}</strong>
            </p>
            <button
              onClick={() => {
                resetForNewDocument();
                addMessage('assistant', 'Session reset. What type of document would you like to create?');
              }}
              className="w-full flex items-center justify-center text-sm text-white bg-yellow-600 hover:bg-yellow-700 font-medium py-2 px-4 rounded"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Start Over / Change Document Type
            </button>
          </div>
        )}
        
        {currentDocKey && (
          <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
            {!isPackage ? (
              <div>
                <button
                  onClick={downloadCurrentDocument}
                  className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download Document
                </button>
                
                {/* New Document button */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <button
                    onClick={() => {
                      resetForNewDocument();
                      addMessage('assistant', 'Ready to help you create a new document! What type of document would you like to create?');
                    }}
                    className="w-full flex items-center justify-center text-sm text-white bg-green-600 hover:bg-green-700 font-medium py-2 px-4 rounded"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Start New Document
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Disclaimer Package Documents:
                </p>
                <div className="space-y-2">
                  {messages.map((msg, idx) => {
                    if (msg.packageUrls) {
                      const sortedDocs = Object.entries(msg.packageUrls).sort((a, b) => {
                        // Define the order
                        const order = ['Will', 'Trust', 'Financial Power of Attorney', 'Healthcare Power of Attorney', 'HIPAA Authorization'];
                        return order.indexOf(a[0]) - order.indexOf(b[0]);
                      });
                      
                      return sortedDocs.map(([docType, url], docIdx) => (
                        <div key={`${docType}-${idx}`} className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 hover:border-blue-300 transition-colors">
                          <span className="text-sm font-medium text-gray-700">{docType}</span>
                          <a 
                            href={url.startsWith('/') ? `${apiEndpoint}${url}` : url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-sm text-blue-600 hover:text-blue-800 font-medium"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Download
                          </a>
                        </div>
                      ));
                    }
                    return null;
                  })}
                  
                  {/* Add a download all button */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <button
                      onClick={downloadAllAsZip}
                      className="w-full flex items-center justify-center text-sm text-white bg-blue-600 hover:bg-blue-700 font-medium py-2 px-4 rounded"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                      </svg>
                      Download All Documents (ZIP)
                    </button>
                    
                    {/* New Document button for packages */}
                    <button
                      onClick={() => {
                        resetForNewDocument();
                        addMessage('assistant', 'Ready to help you create a new document! What type of document would you like to create?');
                      }}
                      className="w-full mt-2 flex items-center justify-center text-sm text-white bg-green-600 hover:bg-green-700 font-medium py-2 px-4 rounded"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Start New Document
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatInterface;